import React from 'react';
import { motion } from 'framer-motion';
import { 
  Target, Rocket, AlertTriangle, 
  CheckCircle, TrendingUp 
} from 'lucide-react';

const InteractiveRoadmap = ({ roadmap }) => {
  if (!roadmap) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Roadmap analysis not available</p>
      </div>
    );
  }

  const Column = ({ title, items, icon: Icon, color, bgColor, borderColor }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-5 rounded-2xl ${bgColor} border ${borderColor} h-full`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-xl bg-slate-800/50`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <h3 className={`font-heading font-semibold ${color}`}>{title}</h3>
      </div>
      <ul className="space-y-3">
        {(items || []).map((item, index) => (
          <motion.li
            key={index}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-start gap-3"
          >
            <div className={`w-2 h-2 rounded-full ${color.replace('text-', 'bg-')} mt-2 shrink-0`} />
            <p className="text-sm text-slate-300 leading-relaxed">{item}</p>
          </motion.li>
        ))}
        {(!items || items.length === 0) && (
          <li className="text-sm text-slate-500 italic">Not clearly stated</li>
        )}
      </ul>
    </motion.div>
  );

  return (
    <div className="space-y-6" data-testid="interactive-roadmap">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-slate-800">
          <Target className="w-5 h-5 text-teal-400" />
        </div>
        <h2 className="text-xl font-heading font-bold text-white">Research Roadmap</h2>
      </div>

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Column
          title="Future Prospects"
          items={roadmap.future_prospects}
          icon={TrendingUp}
          color="text-emerald-400"
          bgColor="bg-emerald-400/5"
          borderColor="border-emerald-400/20"
        />

        <Column
          title="Limitations"
          items={roadmap.limitations}
          icon={AlertTriangle}
          color="text-orange-400"
          bgColor="bg-orange-400/5"
          borderColor="border-orange-400/20"
        />

        <Column
          title="Next Steps"
          items={roadmap.next_steps}
          icon={CheckCircle}
          color="text-teal-400"
          bgColor="bg-teal-400/5"
          borderColor="border-teal-400/20"
        />
      </div>
    </div>
  );
};

export default InteractiveRoadmap;
